// AI Brand Assistant — content-team workspace with three modes:
//   • Chat       — live LLM Q&A grounded in the tenant's scan data (/assistant/ask/)
//   • AI Review  — NOVA AI scans the last N days of mentions and flags
//                  anything worth a human look (/assistant/ai-review/)
//   • Compare    — side-by-side metrics + insights for 2-4 posts
//                  (/assistant/ai-compare/)
import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
    bp_assistant_ask,
    bp_ai_review_posts,
    bp_ai_compare_posts,
    bp_mentions,
} from '../../../services';
import { unwrap, mdNormalize } from './_shared';
import { Icon } from './_icons';

const LS_KEY = 'bm_asst_chats_v1';

const STROKE = '#64748b';

// Light-theme severity palette — matches Threat Monitor (saturated 700-tier
// text so it stays legible on white).
const SEV = {
    high:   { text: '#b91c1c', bg: '#fef2f2', border: '#fecaca', label: 'High' },
    medium: { text: '#b45309', bg: '#fffbeb', border: '#fde68a', label: 'Monitor' },
    low:    { text: '#a16207', bg: '#fefce8', border: '#fef08a', label: 'Low' },
};

const SENT_COLOR = { positive: '#16a34a', negative: '#dc2626', neutral: '#64748b' };

const ICON_SUMMARY = (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
        <rect x="2.5" y="2.5" width="11" height="11" rx="1.5"
              fill="none" stroke={STROKE} strokeWidth="1.2" />
        <line x1="5" y1="6" x2="11" y2="6" stroke={STROKE} strokeWidth="1.2" />
        <line x1="5" y1="8.5" x2="11" y2="8.5" stroke={STROKE} strokeWidth="1.2" />
        <line x1="5" y1="11" x2="9" y2="11" stroke={STROKE} strokeWidth="1.2" />
    </svg>
);

const ICON_COMPETITOR = (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
        <rect x="2"  y="9" width="4" height="5" fill={STROKE} opacity="0.55" />
        <rect x="10" y="5" width="4" height="9" fill={STROKE} />
    </svg>
);

const ICON_GROWTH = (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
        <polyline points="2,12 6,8 9,10 14,4"
                  fill="none" stroke={STROKE} strokeWidth="1.4"
                  strokeLinecap="round" strokeLinejoin="round" />
        <polyline points="11,4 14,4 14,7"
                  fill="none" stroke={STROKE} strokeWidth="1.4"
                  strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ICON_SPIKE = (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
        <polyline points="1,12 4,11 6,6 8,13 10,9 13,10 15,8"
                  fill="none" stroke={STROKE} strokeWidth="1.3"
                  strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ICON_LEADS = (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="8" r="6"   fill="none" stroke={STROKE} strokeWidth="1.2" />
        <circle cx="8" cy="8" r="3.2" fill="none" stroke={STROKE} strokeWidth="1.2" />
        <circle cx="8" cy="8" r="1.2" fill={STROKE} />
    </svg>
);

const QUICK_STARTS = [
    { key: 'summary',     icon: ICON_SUMMARY,    label: 'Project Summary',
      prompt: 'Give me a project summary of brand mentions in the last 14 days.' },
    { key: 'competitor',  icon: ICON_COMPETITOR, label: 'Competitor Impact',
      prompt: 'How does my brand mention volume compare to the prior period?' },
    { key: 'growth',      icon: ICON_GROWTH,     label: 'Brand Growth Strategy',
      prompt: 'Suggest a brand growth strategy based on recent topic trends.' },
    { key: 'spikes',      icon: ICON_SPIKE,      label: 'Engagement Spikes',
      prompt: 'When did engagement spike, and what topics drove it?' },
    { key: 'leads',       icon: ICON_LEADS,      label: 'Lead Opportunities',
      prompt: 'What positive-sentiment posts look like lead opportunities?' },
];

function uid() {
    return 'c_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 6);
}

function chatTitle(thread) {
    const first = thread.find(m => m.role === 'user');
    if (!first) return 'New chat';
    return first.content.slice(0, 50) + (first.content.length > 50 ? '…' : '');
}

function fmtDate(iso) {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleDateString(undefined,
            { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (_) { return iso; }
}

// ---------------------------------------------------------------------------
// Mode switcher (segmented control)
// ---------------------------------------------------------------------------
const MODES = [
    { key: 'chat',    label: 'Chat' },
    { key: 'review',  label: 'NOVA AI Review' },
    { key: 'compare', label: 'Compare Posts' },
];

function ModeSwitcher({ mode, setMode }) {
    return (
        <div style={{ display: 'inline-flex', gap: 2, padding: 3, borderRadius: 10,
                      background: '#f1f5f9', border: '1px solid #e2e8f0',
                      marginBottom: 20 }}>
            {MODES.map(m => {
                const active = m.key === mode;
                return (
                    <button key={m.key} onClick={() => setMode(m.key)}
                            style={{
                                border: 'none', cursor: 'pointer',
                                padding: '7px 16px', borderRadius: 8, fontSize: 13,
                                fontWeight: active ? 600 : 500,
                                color: active ? '#0f172a' : '#64748b',
                                background: active ? '#fff' : 'transparent',
                                boxShadow: active ? '0 1px 2px rgba(15,23,42,0.08)' : 'none',
                            }}>
                        {m.label}
                    </button>
                );
            })}
        </div>
    );
}

// ===========================================================================
// CHAT MODE — localStorage-backed multi-chat Q&A.
// Layout follows mainstream assistant UIs (ChatGPT / Claude / Intercom Fin):
// scrollable message log with avatars + markdown, composer pinned at bottom,
// copy-on-hover, auto-scroll, Enter-to-send.
// ===========================================================================
const NovaAvatar = () => (
    <span className="bm_msg_avatar nova" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 32 32">
            <circle cx="16" cy="16" r="12" fill="none" stroke="#7c3aed" strokeWidth="2" opacity="0.55" />
            <circle cx="16" cy="16" r="6"  fill="none" stroke="#7c3aed" strokeWidth="2" />
            <circle cx="16" cy="16" r="2"  fill="#7c3aed" />
        </svg>
    </span>
);

function CopyButton({ text }) {
    const [done, setDone] = useState(false);
    const copy = () => {
        try {
            navigator.clipboard.writeText(text || '');
            setDone(true);
            setTimeout(() => setDone(false), 1400);
        } catch (_) { /* ignore */ }
    };
    return (
        <button type="button" className="bm_copy_btn" onClick={copy} title="Copy answer">
            <Icon name={done ? 'check' : 'message'} size={12} />
            {done ? 'Copied' : 'Copy'}
        </button>
    );
}

const TypingDots = () => (
    <span className="bm_typing" aria-label="Assistant is thinking">
        <span /><span /><span />
    </span>
);

function ChatMessage({ msg }) {
    const isUser = msg.role === 'user';
    return (
        <div className={`bm_msg_row ${isUser ? 'user' : 'assistant'}`}>
            {isUser
                ? <span className="bm_msg_avatar user" aria-hidden="true">You</span>
                : <NovaAvatar />}
            <div className="bm_msg_body">
                <div className="bm_msg_name">{isUser ? 'You' : 'NOVA AI'}</div>
                {isUser
                    ? <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13.5, lineHeight: 1.55 }}>{msg.content}</div>
                    : <div className="bm_md"><ReactMarkdown>{mdNormalize(msg.content)}</ReactMarkdown></div>}
                {!isUser && !msg.error && (
                    <div className="bm_msg_actions"><CopyButton text={msg.content} /></div>
                )}
            </div>
        </div>
    );
}

function ChatMode() {
    const [chats, setChats]       = useState([]);  // [{id, thread, updatedAt}]
    const [activeId, setActiveId] = useState(null);
    const [input, setInput]       = useState('');
    const [busy, setBusy]         = useState(false);
    const endRef = useRef(null);
    const taRef  = useRef(null);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(LS_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed) && parsed.length) {
                    setChats(parsed);
                    setActiveId(parsed[0].id);
                    return;
                }
            }
        } catch (_) { /* ignore */ }
        const fresh = { id: uid(), thread: [], updatedAt: Date.now() };
        setChats([fresh]);
        setActiveId(fresh.id);
    }, []);

    useEffect(() => {
        if (chats.length === 0) return;
        try { localStorage.setItem(LS_KEY, JSON.stringify(chats)); } catch (_) {}
    }, [chats]);

    const activeChat = chats.find(c => c.id === activeId) || { thread: [] };
    const thread = activeChat.thread || [];

    const updateActive = (mutator) => {
        setChats(prev => prev.map(c =>
            c.id === activeId ? { ...c, thread: mutator(c.thread || []), updatedAt: Date.now() } : c
        ));
    };

    const newChat = () => {
        const fresh = { id: uid(), thread: [], updatedAt: Date.now() };
        setChats(prev => [fresh, ...prev]);
        setActiveId(fresh.id);
        setInput('');
    };

    const deleteChat = (id) => {
        setChats(prev => {
            const next = prev.filter(c => c.id !== id);
            if (next.length === 0) {
                const fresh = { id: uid(), thread: [], updatedAt: Date.now() };
                setActiveId(fresh.id);
                return [fresh];
            }
            if (id === activeId) setActiveId(next[0].id);
            return next;
        });
    };

    const send = async (text) => {
        if (!text?.trim() || busy) return;
        const userMsg = { role: 'user', content: text.trim() };
        const priorTurns = thread.map(m => ({ role: m.role, content: m.content }));
        updateActive(t => [...t, userMsg]);
        setInput('');
        setBusy(true);
        try {
            const res = await bp_assistant_ask({ question: text.trim(), thread: priorTurns });
            const data = unwrap(res) || {};
            updateActive(t => [...t, {
                role: 'assistant',
                content: data.answer || 'No answer generated.',
            }]);
        } catch (e) {
            updateActive(t => [...t, {
                role: 'assistant',
                content: 'The assistant is temporarily unavailable. Please try again.',
                error: true,
            }]);
        } finally {
            setBusy(false);
        }
    };

    const onSubmit = (e) => { e.preventDefault(); send(input); };
    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
    };
    const grow = (el) => {
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, 140) + 'px';
    };

    // Auto-scroll to the newest message / typing indicator.
    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [thread.length, busy]);

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 16,
                      alignItems: 'stretch' }}>
            {/* Left: chat history sidebar */}
            <div style={{ borderRight: '1px solid #e2e8f0', paddingRight: 12 }}>
                <button onClick={newChat} className="btn btn-sm btn-primary"
                        style={{ width: '100%', marginBottom: 8,
                                 display: 'inline-flex', alignItems: 'center',
                                 justifyContent: 'center', gap: 6 }}>
                    <Icon name="message" size={13} color="#fff" /> New chat
                </button>
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase',
                              letterSpacing: 0.4, fontWeight: 600, marginBottom: 6,
                              padding: '4px 6px' }}>
                    Chat history
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 560,
                              overflowY: 'auto' }}>
                    {chats.map(c => {
                        const isActive = c.id === activeId;
                        return (
                            <div key={c.id} onClick={() => setActiveId(c.id)}
                                 style={{
                                     padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                                     background: isActive ? 'rgba(59, 130, 246, 0.12)' : 'transparent',
                                     border: isActive ? '1px solid rgba(59, 130, 246, 0.4)' : '1px solid transparent',
                                     fontSize: 12, color: isActive ? '#1e3a8a' : '#334155',
                                     position: 'relative',
                                 }}>
                                <div style={{ paddingRight: 18, wordBreak: 'break-word',
                                              fontWeight: isActive ? 600 : 500 }}>
                                    {chatTitle(c.thread)}
                                </div>
                                <button onClick={(e) => { e.stopPropagation(); deleteChat(c.id); }}
                                        title="Delete this chat"
                                        style={{
                                            position: 'absolute', top: 4, right: 4,
                                            background: 'transparent', border: 'none',
                                            color: '#94a3b8', fontSize: 14, cursor: 'pointer',
                                            padding: '2px 6px', lineHeight: 1,
                                        }}>×</button>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Right: chat surface — scrollable log + pinned composer */}
            <div className="bm_chat">
                {thread.length === 0 ? (
                    <div className="bm_chat_empty">
                        <div className="bm_chat_hero" aria-hidden="true">
                            <svg width="40" height="40" viewBox="0 0 32 32">
                                <circle cx="16" cy="16" r="13" fill="none" stroke="#7c3aed" strokeWidth="1.5" opacity="0.6" />
                                <circle cx="16" cy="16" r="7"  fill="none" stroke="#7c3aed" strokeWidth="1.5" />
                                <circle cx="16" cy="16" r="2.2" fill="#7c3aed" />
                            </svg>
                        </div>
                        <h3 style={{ margin: '14px 0 6px', fontSize: 22, color: '#0f172a' }}>
                            Hello, how can I help you?
                        </h3>
                        <p style={{ margin: 0, color: '#64748b', fontSize: 13, maxWidth: 460 }}>
                            Ask about your brand's mentions, sentiment, narratives, sources and
                            influencers from the last 14 days — or start with a suggestion below.
                        </p>
                        <div className="bm_qs_grid">
                            {QUICK_STARTS.map(q => (
                                <button key={q.key} className="bm_qs_card"
                                        onClick={() => send(q.prompt)} disabled={busy}>
                                    <span className="bm_qs_icon">{q.icon}</span>
                                    <span>{q.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="bm_chat_msgs">
                        {thread.map((msg, i) => <ChatMessage key={i} msg={msg} />)}
                        {busy && (
                            <div className="bm_msg_row assistant">
                                <NovaAvatar />
                                <div className="bm_msg_body">
                                    <div className="bm_msg_name">NOVA AI</div>
                                    <TypingDots />
                                </div>
                            </div>
                        )}
                        <div ref={endRef} />
                    </div>
                )}

                <div className="bm_chat_composer">
                    <form onSubmit={onSubmit} className="bm_composer_box">
                        <textarea
                            ref={taRef}
                            rows={1}
                            placeholder="Ask me any brand-related question…"
                            value={input}
                            onChange={e => { setInput(e.target.value); grow(taRef.current); }}
                            onKeyDown={onKeyDown}
                            disabled={busy} />
                        <button type="submit" className="bm_send_btn"
                                disabled={busy || !input.trim()} title="Send (Enter)">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" strokeWidth="2"
                                 strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="19" x2="12" y2="5" />
                                <polyline points="5 12 12 5 19 12" />
                            </svg>
                        </button>
                    </form>
                    <div className="bm_composer_hint">
                        Enter to send · Shift+Enter for a new line · grounded in your last 14 days of
                        scan data. NOVA AI can make mistakes.
                    </div>
                </div>
            </div>
        </div>
    );
}

// ===========================================================================
// REVIEW MODE — NOVA AI scans the last N days and flags suspicious posts.
// ===========================================================================
function SevBadge({ severity }) {
    const c = SEV[severity] || SEV.low;
    return (
        <span style={{
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: 0.4, color: c.text, background: c.bg,
            border: `1px solid ${c.border}`, borderRadius: 4, padding: '2px 7px',
        }}>{c.label}</span>
    );
}

function ReviewCard({ f }) {
    const c = SEV[f.severity] || SEV.low;
    return (
        <div style={{
            border: `1px solid ${c.border}`, borderLeft: `3px solid ${c.text}`,
            borderRadius: 8, padding: '14px 16px', marginBottom: 10, background: '#fff',
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <SevBadge severity={f.severity} />
                    <span style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>
                        @{f.handle}
                    </span>
                    <span style={{ fontSize: 12, color: '#64748b' }}>
                        · {f.platform} · {fmtDate(f.posted_at)}
                    </span>
                </div>
                <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>
                    risk {f.risk_score} · reach {f.reach_score}
                </span>
            </div>

            <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.5, marginBottom: 10 }}>
                {f.snippet}{(f.snippet || '').length >= 280 ? '…' : ''}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {(f.reasons || []).map((r, i) => (
                    <span key={i} style={{
                        fontSize: 11, color: c.text, background: c.bg,
                        border: `1px solid ${c.border}`, borderRadius: 4, padding: '2px 8px',
                    }}>{r}</span>
                ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', gap: 12, borderTop: '1px solid #f1f5f9',
                          paddingTop: 10 }}>
                <div style={{ fontSize: 12, color: '#475569' }}>
                    <strong style={{ color: '#0f172a' }}>Suggested action:</strong> {f.suggested_action}
                </div>
                {f.url && (
                    <a href={f.url} target="_blank" rel="noopener noreferrer"
                       style={{ fontSize: 12, color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                        View source
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
                             strokeLinejoin="round" style={{ marginLeft: 4, verticalAlign: 'middle' }}>
                            <path d="M7 17L17 7" /><polyline points="7 7 17 7 17 17" />
                        </svg>
                    </a>
                )}
            </div>
        </div>
    );
}

function ReviewMode() {
    const [days, setDays]   = useState(15);
    const [busy, setBusy]   = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const run = async () => {
        setBusy(true); setError(null);
        try {
            const res = await bp_ai_review_posts({ days });
            setResult(unwrap(res) || {});
        } catch (e) {
            setError('Review failed — please try again.');
        } finally {
            setBusy(false);
        }
    };

    const bySev = result?.by_severity || {};

    return (
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: 18, color: '#0f172a' }}>NOVA AI Review</h3>
                    <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                        Scans your recent mentions and flags anything the content team should act on.
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <select value={days} onChange={e => setDays(Number(e.target.value))}
                            disabled={busy}
                            style={{ padding: '7px 10px', borderRadius: 8,
                                     border: '1px solid #cbd5e1', fontSize: 13, color: '#0f172a' }}>
                        <option value={7}>Last 7 days</option>
                        <option value={15}>Last 15 days</option>
                        <option value={30}>Last 30 days</option>
                    </select>
                    <button onClick={run} disabled={busy} className="btn btn-sm btn-primary">
                        {busy ? 'Reviewing…' : 'Run review'}
                    </button>
                </div>
            </div>

            {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca',
                              color: '#b91c1c', borderRadius: 8, padding: '10px 14px',
                              fontSize: 13 }}>{error}</div>
            )}

            {!result && !busy && !error && (
                <div style={{ textAlign: 'center', padding: '48px 20px', color: '#64748b' }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                        Ready when you are.
                    </div>
                    <div style={{ fontSize: 13 }}>
                        Pick a window and run a review. NOVA AI weighs sentiment, author risk,
                        emotion, narrative and amplification — and tells you why each post was flagged.
                    </div>
                </div>
            )}

            {result && (
                <>
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0',
                                  borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                        <div style={{ fontSize: 14, color: '#0f172a', lineHeight: 1.5 }}>
                            {result.headline}
                        </div>
                        <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 12,
                                      color: '#64748b', flexWrap: 'wrap' }}>
                            <span>Reviewed <strong style={{ color: '#0f172a' }}>{result.total_reviewed}</strong> mentions</span>
                            <span style={{ color: SEV.high.text }}>High <strong>{bySev.high || 0}</strong></span>
                            <span style={{ color: SEV.medium.text }}>Monitor <strong>{bySev.medium || 0}</strong></span>
                            <span style={{ color: SEV.low.text }}>Low <strong>{bySev.low || 0}</strong></span>
                        </div>
                    </div>

                    {(result.flagged || []).length === 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      gap: 8, padding: '32px 20px', color: '#16a34a', fontSize: 14 }}>
                            <Icon name="shield" size={18} color="#16a34a" />
                            Nothing suspicious in this window.
                        </div>
                    ) : (
                        (result.flagged || []).map(f => <ReviewCard key={f.id} f={f} />)
                    )}
                </>
            )}
        </div>
    );
}

// ===========================================================================
// COMPARE MODE — pick 2-4 posts, get side-by-side metrics + insights.
// ===========================================================================
function CompareMode() {
    const [candidates, setCandidates] = useState([]);
    const [loadingCands, setLoadingCands] = useState(false);
    const [selected, setSelected] = useState([]);  // array of mention ids
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        setLoadingCands(true);
        bp_mentions({ page_size: 30, sort: 'relevance' })
            .then(res => {
                if (!alive) return;
                const data = unwrap(res) || {};
                setCandidates(Array.isArray(data.results) ? data.results : []);
            })
            .catch(() => { if (alive) setError('Could not load recent posts.'); })
            .finally(() => { if (alive) setLoadingCands(false); });
        return () => { alive = false; };
    }, []);

    const toggle = (id) => {
        setSelected(prev => {
            if (prev.includes(id)) return prev.filter(x => x !== id);
            if (prev.length >= 4) return prev;  // cap at 4
            return [...prev, id];
        });
    };

    const compare = async () => {
        if (selected.length < 2) return;
        setBusy(true); setError(null); setResult(null);
        try {
            const res = await bp_ai_compare_posts({ ids: selected });
            setResult(unwrap(res) || {});
        } catch (e) {
            setError('Comparison failed — please try again.');
        } finally {
            setBusy(false);
        }
    };

    const insights = result?.insights || {};
    const badgeFor = (id) => {
        const b = [];
        if (insights.best_reach?.id === id)    b.push({ t: 'Top reach', c: '#1d4ed8' });
        if (insights.most_positive?.id === id) b.push({ t: 'Most positive', c: '#16a34a' });
        if (insights.highest_risk?.id === id)  b.push({ t: 'Highest risk', c: '#b91c1c' });
        return b;
    };

    return (
        <div style={{ maxWidth: 980, margin: '0 auto' }}>
            <div style={{ marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 18, color: '#0f172a' }}>Compare Posts</h3>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                    Select 2–4 posts to compare reach, sentiment and risk side by side.
                </div>
            </div>

            {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca',
                              color: '#b91c1c', borderRadius: 8, padding: '10px 14px',
                              fontSize: 13, marginBottom: 12 }}>{error}</div>
            )}

            {/* Selector */}
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                              alignItems: 'center', padding: '10px 14px',
                              borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#475569',
                                   textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        Recent posts {selected.length > 0 && `· ${selected.length}/4 selected`}
                    </span>
                    <button onClick={compare} disabled={busy || selected.length < 2}
                            className="btn btn-sm btn-primary">
                        {busy ? 'Comparing…' : 'Compare selected'}
                    </button>
                </div>
                <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                    {loadingCands && (
                        <div style={{ padding: 16, fontSize: 13, color: '#64748b' }}>Loading posts…</div>
                    )}
                    {!loadingCands && candidates.length === 0 && (
                        <div style={{ padding: 16, fontSize: 13, color: '#64748b' }}>
                            No recent posts to compare.
                        </div>
                    )}
                    {candidates.map(m => {
                        const checked = selected.includes(m.id);
                        const capped = !checked && selected.length >= 4;
                        return (
                            <label key={m.id}
                                   style={{
                                       display: 'flex', alignItems: 'flex-start', gap: 10,
                                       padding: '10px 14px', borderBottom: '1px solid #f8fafc',
                                       cursor: capped ? 'not-allowed' : 'pointer',
                                       background: checked ? 'rgba(59,130,246,0.06)' : 'transparent',
                                       opacity: capped ? 0.5 : 1,
                                   }}>
                                <input type="checkbox" checked={checked} disabled={capped}
                                       onChange={() => toggle(m.id)} style={{ marginTop: 3 }} />
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>
                                        <strong style={{ color: '#334155' }}>@{m.handle}</strong>
                                        {' · '}{m.platform}{' · '}{fmtDate(m.posted_at)}
                                        {' · '}<span style={{ color: SENT_COLOR[m.sentiment] || '#64748b' }}>
                                            {m.sentiment}
                                        </span>
                                        {' · reach '}{m.reach_score}
                                    </div>
                                    <div style={{ fontSize: 13, color: '#334155',
                                                  overflow: 'hidden', textOverflow: 'ellipsis',
                                                  whiteSpace: 'nowrap' }}>
                                        {m.text}
                                    </div>
                                </div>
                            </label>
                        );
                    })}
                </div>
            </div>

            {/* Results */}
            {result && Array.isArray(result.posts) && result.posts.length > 0 && (
                <>
                    {(insights.notes || []).length > 0 && (
                        <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe',
                                      borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed',
                                          textTransform: 'uppercase', letterSpacing: 0.4,
                                          marginBottom: 8 }}>
                                NOVA AI insights
                            </div>
                            <ul style={{ margin: 0, paddingLeft: 18, color: '#334155',
                                         fontSize: 13, lineHeight: 1.6 }}>
                                {insights.notes.map((n, i) => <li key={i}>{n}</li>)}
                            </ul>
                        </div>
                    )}

                    <div style={{
                        display: 'grid', gap: 12,
                        gridTemplateColumns: `repeat(${result.posts.length}, minmax(0, 1fr))`,
                    }}>
                        {result.posts.map(p => (
                            <div key={p.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8,
                                                      padding: 14, background: '#fff' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                                    {badgeFor(p.id).map((b, i) => (
                                        <span key={i} style={{
                                            fontSize: 10, fontWeight: 700, color: b.c,
                                            border: `1px solid ${b.c}`, borderRadius: 4,
                                            padding: '1px 6px', textTransform: 'uppercase',
                                            letterSpacing: 0.3,
                                        }}>{b.t}</span>
                                    ))}
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                                    @{p.handle}
                                </div>
                                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
                                    {p.platform} · {fmtDate(p.posted_at)}
                                </div>
                                <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.5,
                                              marginBottom: 12, maxHeight: 96, overflow: 'hidden' }}>
                                    {p.snippet}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6,
                                              borderTop: '1px solid #f1f5f9', paddingTop: 10,
                                              fontSize: 12 }}>
                                    <Metric label="Reach" value={p.reach_score} />
                                    <Metric label="Followers" value={(p.followers || 0).toLocaleString()} />
                                    <Metric label="Sentiment" value={p.sentiment}
                                            color={SENT_COLOR[p.sentiment]} />
                                    {p.emotion && <Metric label="Emotion" value={p.emotion} />}
                                    {p.narrative_tag && <Metric label="Narrative" value={p.narrative_tag} />}
                                    <Metric label="Risk source" value={p.is_risk_tagged_source ? 'Yes' : 'No'}
                                            color={p.is_risk_tagged_source ? '#b91c1c' : undefined} />
                                </div>
                                {p.url && (
                                    <a href={p.url} target="_blank" rel="noopener noreferrer"
                                       style={{ display: 'inline-block', marginTop: 10,
                                                fontSize: 12, color: '#1d4ed8' }}>
                                        View source
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
                                             strokeLinejoin="round" style={{ marginLeft: 4, verticalAlign: 'middle' }}>
                                            <path d="M7 17L17 7" /><polyline points="7 7 17 7 17 17" />
                                        </svg>
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function Metric({ label, value, color }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ color: '#94a3b8' }}>{label}</span>
            <span style={{ color: color || '#0f172a', fontWeight: 600, textAlign: 'right' }}>
                {value}
            </span>
        </div>
    );
}

// ===========================================================================
export default function AssistantTab() {
    const [mode, setMode] = useState('chat');
    return (
        <div>
            <ModeSwitcher mode={mode} setMode={setMode} />
            {mode === 'chat'    && <ChatMode />}
            {mode === 'review'  && <ReviewMode />}
            {mode === 'compare' && <CompareMode />}
        </div>
    );
}
